import { subscribeToParentCmp } from '@shared/js/consent/parentCmp';

const CONSENT_CHECK_INTERVAL_MS = 200;
const EMBED_CMP_RESPONSE_TIMEOUT_MS = 3000;
const READY_EVENT_STATUSES = ['tcloaded', 'useractioncomplete'];
const MZ_COOKIE_CONSENT_MODE = Object.freeze({
	NOT_REQUIRED: 'notRequired',
	LOCAL_CMP:    'localCmp',
	PARENT_CMP:   'parentCmp',
});
let consentDecisionPromise = null;

// Keeps MZ consent eligibility in one place while callers apply it at their appropriate UI lifecycle boundary.
const getMzCookieConsentMode = () => {
	const isManagerDomain = window.managerDomain && window.location.host === window.managerDomain;
	if (!window.isMz || isManagerDomain) return MZ_COOKIE_CONSENT_MODE.NOT_REQUIRED;

	return window.isEmbed ? MZ_COOKIE_CONSENT_MODE.PARENT_CMP : MZ_COOKIE_CONSENT_MODE.LOCAL_CMP;
};

const isConsentDecisionReady = (tcData, success) => {
	return success && tcData && READY_EVENT_STATUSES.includes(tcData.eventStatus);
};

// A missing parent CMP times out, but a responding CMP can keep the player waiting behind its dialog.
const waitForParentCmp = () => new Promise((resolve) => {
	let wasResolved = false;
	let unsubscribe = () => {};
	const timeout = setTimeout(() => {
		wasResolved = true;
		unsubscribe();
		resolve(false);
	}, EMBED_CMP_RESPONSE_TIMEOUT_MS);
	unsubscribe = subscribeToParentCmp((state) => {
		if (!state.success || !state.tcData) return;
		clearTimeout(timeout);
		if (!isConsentDecisionReady(state.tcData, state.success)) return;
		wasResolved = true;
		unsubscribe();
		resolve(true);
	});
	// A shared connection may replay readiness synchronously before unsubscribe has been assigned.
	if (wasResolved) unsubscribe();
});

// Waits for the MZ CMP decision before Google IMA is initialized, using the parent CMP for framed embeds.
const waitForMzCookieConsent = () => {
	const consentMode = getMzCookieConsentMode();
	if (consentMode === MZ_COOKIE_CONSENT_MODE.NOT_REQUIRED) return Promise.resolve(true);
	if (consentDecisionPromise) return consentDecisionPromise;
	if (consentMode === MZ_COOKIE_CONSENT_MODE.PARENT_CMP) {
		consentDecisionPromise = waitForParentCmp();
		return consentDecisionPromise;
	}

	consentDecisionPromise = new Promise((resolve) => {
		let wasResolved = false;
		let consentCheckInterval = null;
		let localCmpListenerWasRegistered = false;
		const resolveConsent = (consentDecisionWasReceived) => {
			if (wasResolved) return;

			wasResolved = true;
			clearInterval(consentCheckInterval);

			if (localCmpListenerWasRegistered && typeof window.__cmp === 'function') {
				try {
					window.__cmp('removeEventListener', ['consent', handleLocalCmpConsent, false], null);
				} catch {
					// The CMP can be replaced during navigation after it has returned a decision.
				}
			}

			resolve(consentDecisionWasReceived);
		};

		const handleLocalCmpConsent = () => resolveConsent(true);

		const checkLocalCmpConsent = () => {
			if (typeof window.__cmp !== 'function') return;

			if (!localCmpListenerWasRegistered) {
				try {
					localCmpListenerWasRegistered = true;
					window.__cmp('addEventListener', ['consent', handleLocalCmpConsent, false], null);
				} catch {
					localCmpListenerWasRegistered = false;
					return;
				}
			}

			try {
				const consentStatus = window.__cmp('consentStatus', null, null, false);
				if (consentStatus?.userChoiceExists) resolveConsent(true);
			} catch {
				// The CMP stub can temporarily reject commands while the full implementation is loading.
			}
		};

		consentCheckInterval = setInterval(checkLocalCmpConsent, CONSENT_CHECK_INTERVAL_MS);
		checkLocalCmpConsent();
	});

	return consentDecisionPromise;
};

export { getMzCookieConsentMode, MZ_COOKIE_CONSENT_MODE, waitForMzCookieConsent };
